import { useState } from 'react';
import { toast } from 'sonner';

import { QueryError } from '@/components/QueryError';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { canManageRole } from '@/lib/can-manage-role';
import { apiErrorMessage } from '@/lib/clients';
import { useHasPermission } from '@/lib/permissions';
import { ROLE_COLORS, ROLE_LABELS } from '@/lib/role-display';
import {
  memberName, useInvitations, useResendInvitation, useUsersByIds, useRevokeInvitation, type Invitation,
} from '@/lib/users';
import { useAuth } from '@/store/auth';

const day = (d: string) => new Date(d).toLocaleDateString('fr-FR');

/** Invitations en attente (PENDING) : renvoi (nouveau lien, 7 jours) ou révocation. */
export function InvitationsList() {
  const myRole = useAuth((s) => s.session?.user.role);
  const canInvite = useHasPermission('users.invite');
  const query = useInvitations();
  // La liste ne renvoie que invitedById : noms résolus en un appel ; inviteur parti du tenant → « — »
  const { data: inviters } = useUsersByIds(query.data?.data.map((i) => i.invitedById) ?? []);
  const resend = useResendInvitation();
  const revoke = useRevokeInvitation();
  const [revokeTarget, setRevokeTarget] = useState<Invitation | null>(null);

  const inviter = (id: string | null) => {
    const m = id ? inviters?.get(id) : undefined;
    return m ? memberName(m) : '—';
  };

  const onResend = (inv: Invitation) =>
    resend.mutate(inv.id, {
      onSuccess: () => toast.success(`Invitation renvoyée à ${inv.email}`),
      onError: (err) => toast.error(apiErrorMessage(err, 'Erreur lors du renvoi')),
    });

  const onRevoke = () => {
    if (!revokeTarget) return;
    revoke.mutate(revokeTarget.id, {
      onSuccess: () => {
        toast.success(`Invitation de ${revokeTarget.email} révoquée`);
        setRevokeTarget(null);
      },
      onError: (err) => toast.error(apiErrorMessage(err, 'Erreur lors de la révocation')),
    });
  };

  const { data, isLoading } = query;

  return (
    <div className="space-y-4">
      <QueryError query={query} />
      <div className="bg-card rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Email</TableHead>
              <TableHead>Rôle</TableHead>
              <TableHead>Invité par</TableHead>
              <TableHead>Expire le</TableHead>
              <TableHead className="text-right"><span className="sr-only">Actions</span></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && Array.from({ length: 3 }).map((_, i) => (
              <TableRow key={i}>{Array.from({ length: 5 }).map((__, j) => <TableCell key={j}><Skeleton className="h-4" /></TableCell>)}</TableRow>
            ))}
            {data?.data.map((inv) => {
              const expired = new Date(inv.expiresAt).getTime() < Date.now();
              // Seul un OWNER gère une invitation au rôle OWNER (FINDING-016)
              const actions = canInvite && canManageRole(myRole, inv.role);
              return (
                <TableRow key={inv.id}>
                  <TableCell className="font-medium">{inv.email}</TableCell>
                  <TableCell><Badge className={ROLE_COLORS[inv.role]}>{ROLE_LABELS[inv.role]}</Badge></TableCell>
                  <TableCell className="text-muted-foreground">{inviter(inv.invitedById)}</TableCell>
                  <TableCell className={expired ? 'text-danger' : undefined}>
                    {day(inv.expiresAt)}{expired && ' (expirée)'}
                  </TableCell>
                  <TableCell className="text-right space-x-2">
                    {actions && (
                      <>
                        <Button variant="outline" size="sm" disabled={resend.isPending} onClick={() => onResend(inv)}>
                          Renvoyer
                        </Button>
                        <Button variant="ghost" size="sm" className="text-danger" onClick={() => setRevokeTarget(inv)}>
                          Révoquer
                        </Button>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
            {data && data.data.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">Aucune invitation en attente</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!revokeTarget} onOpenChange={(o) => { if (!o) setRevokeTarget(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Révoquer l&apos;invitation</DialogTitle>
            <DialogDescription>
              Le lien envoyé à {revokeTarget?.email} ne fonctionnera plus. Vous pourrez l&apos;inviter de nouveau.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRevokeTarget(null)}>Annuler</Button>
            <Button variant="destructive" disabled={revoke.isPending} onClick={onRevoke}>
              {revoke.isPending ? 'Révocation…' : 'Confirmer la révocation'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
